<template>
  <Navbar />

  <!-- dynamic content view -->
  <router-view />

  <!-- footer/more section -->
  <More />

</template>

<script>
import More from "./components/More.vue";
import Navbar from "./components/Navbar.vue";

export default {
  name: "App",
  components: {
    More,
    Navbar
  },
};
</script>

<style>
/* ============================================
   BRAND VISUAL IDENTITY - CSS VARIABLES
   ============================================ */

:root {
  /* ========== PRIMARY COLORS ========== */
  --color-stardust-gold: #e8ae1b;
  --color-stardust-gold-light: #f3c14c;

  /* ========== BACKGROUND COLORS ========== */
  --color-deep-ocean: #182226;
  --color-charcoal-mist: #28363c;
  --color-charcoal-mist-70: rgba(40, 54, 60, 0.7);
  --color-charcoal-mist-85: rgba(40, 54, 60, 0.85);
  --color-charcoal-mist-95: rgba(40, 54, 60, 0.95);
  --color-charcoal-mist-50: rgba(40, 54, 60, 0.5);
  --color-charcoal-mist-30: rgba(40, 54, 60, 0.3);

  /* ========== TEXT COLORS ========== */
  --color-pure-white: #fefefe;
  --color-soft-white: rgba(254, 254, 254, 0.8);
  --color-muted-white: rgba(254, 254, 254, 0.6);
  --color-muted-white-70: rgba(254, 254, 254, 0.7);
  --color-muted-white-90: rgba(254, 254, 254, 0.9);
  --color-muted-white-95: rgba(254, 254, 254, 0.95);

  /* ========== NEUTRAL COLORS ========== */
  --color-steel-gray: #666f70;
  --color-light-steel: #818f91;

  /* ========== ACCENT & EFFECTS ========== */
  --color-gold-glow-15: rgba(232, 174, 27, 0.15);
  --color-gold-glow-30: rgba(232, 174, 27, 0.3);
  --color-gold-glow-40: rgba(232, 174, 27, 0.4);
  --color-gold-glow-50: rgba(232, 174, 27, 0.5);

  /* ========== BORDERS ========== */
  --color-border-subtle: rgba(254, 254, 254, 0.1);
  --color-border-subtle-20: rgba(254, 254, 254, 0.2);
  --color-border-subtle-30: rgba(254, 254, 254, 0.3);

  /* ========== DARK TEXT ON GOLD ========== */
  --color-dark-on-gold: #28363c;
  --color-dark-on-gold-70: rgba(40, 54, 60, 0.7);

  /* ========== TYPOGRAPHY ========== */
  --font-primary: Raleway, Inter, Arial, sans-serif;
  --font-secondary: Inter, Arial, sans-serif;

  /* Font Weights */
  --font-weight-light: 300;
  --font-weight-regular: 400;
  --font-weight-medium: 500;
  --font-weight-semibold: 600;
  --font-weight-bold: 700;

  /* ========== SPACING ========== */
  --spacing-xs: 8px;
  --spacing-sm: 12px;
  --spacing-md: 16px;
  --spacing-lg: 24px;
  --spacing-xl: 32px;
  --spacing-2xl: 40px;
  --spacing-3xl: 60px;

  /* ========== BORDER RADIUS ========== */
  --radius-subtle: 8px;
  --radius-medium: 10px;
  --radius-medium-lg: 12px;
  --radius-large: 20px;
  --radius-pill: 50px;
  --radius-pill-full: 150px;

  /* ========== SHADOWS ========== */
  --shadow-brand: drop-shadow(0px 0px 6px var(--color-deep-ocean));
  --shadow-card: drop-shadow(0 4px 4px rgba(0, 0, 0, 0.25));
  --shadow-elevated: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.35));
  --shadow-gold-glow: 0 4px 20px var(--color-gold-glow-30);
  --shadow-gold-glow-hover: 0 8px 30px var(--color-gold-glow-40);
  --shadow-dark: 0 6px 20px rgba(0, 0, 0, 0.3);

  /* ========== TRANSITIONS ========== */
  --transition-standard: all 0.3s ease;
  --transition-premium: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --transition-smooth: transform 0.3s ease;
  --transition-quick: all 0.2s ease;

  /* ========== BLUR EFFECTS ========== */
  --blur-glassmorphism: blur(7px);
  --blur-navbar: blur(15px);
  --blur-dropdown: blur(10px);

  /* ========== CONTAINER ========== */
  --container-max-width: 1200px;
  --container-padding: 20px;
  --container-padding-tablet: 15px;
  --container-padding-mobile: 10px;
}

/* ============================================
   BASE STYLES
   ============================================ */

* {
  box-sizing: border-box;
}

html {
  height: 100%;
  scroll-behavior: smooth;
}

body {
  margin: 0;
  height: 100%;
  background-color: var(--color-deep-ocean) !important;
}

#app {
  font-family: var(--font-primary);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--color-pure-white);
}

/* ============================================
   LAYOUT CONTAINERS
   ============================================ */

.container {
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--container-padding);
}

@media (max-width: 1000px) {
  .container {
    max-width: 970px;
    padding: 0 var(--container-padding-tablet);
  }
}

@media (max-width: 768px) {
  .container {
    padding: 0 var(--container-padding-mobile);
  }
}

@media (max-width: 480px) {
  h1 {
    margin: 30px 0;
  }

  section {
    padding: var(--container-padding-tablet) var(--container-padding-mobile);
  }
}

/* ============================================
   TYPOGRAPHY
   ============================================ */

hr {
  border: 1px solid var(--color-border-subtle);
  border-radius: 5px;
  opacity: 1;
}

h1 {
  text-align: center;
  font-weight: var(--font-weight-bold);
  margin: var(--spacing-3xl) 0;
  font-size: clamp(28px, 5vw, 48px);
  line-height: 1.3;
  color: var(--color-pure-white);
}

h2 {
  font-size: clamp(22px, 4vw, 36px);
  line-height: 1.4;
  font-weight: var(--font-weight-semibold);
  color: var(--color-pure-white);
}

h3 {
  font-size: clamp(18px, 3vw, 28px);
  line-height: 1.4;
  font-weight: var(--font-weight-semibold);
  color: var(--color-pure-white);
}

p {
  font-family: var(--font-secondary);
  font-size: clamp(14px, 2vw, 18px);
  line-height: 1.6;
  font-weight: var(--font-weight-regular);
  color: var(--color-pure-white);
}

a {
  color: var(--color-stardust-gold);
  transition: var(--transition-quick);
}

a:hover {
  color: var(--color-stardust-gold-light);
}

a:focus-visible {
  outline: 2px solid var(--color-stardust-gold);
  outline-offset: 3px;
  border-radius: 3px;
}

.highlight {
  color: var(--color-stardust-gold);
}

.stardust-title {
  color: var(--color-stardust-gold);
  font-family: Raleway;
  font-weight: var(--font-weight-semibold);
  -webkit-filter: var(--shadow-brand);
  filter: var(--shadow-brand);
}

h4.sectionTitle {
  font-weight: var(--font-weight-semibold);
  color: var(--color-pure-white);
}

/* ============================================
   ROUTER NAVIGATION LINKS
   ============================================ */

#nav a {
  color: var(--color-steel-gray);
  text-decoration: none;
}

#nav a:hover {
  color: var(--color-pure-white);
}

#nav a.router-link-exact-active {
  color: var(--color-stardust-gold);
  text-decoration: none;
}

/* ============================================
   BUTTON STYLES
   ============================================ */

/* Bootstrap Override */
.btn-outline-dark {
  background-color: var(--color-deep-ocean);
  color: var(--color-stardust-gold);
  font-size: 1.3rem;
  padding: 1rem 1.3rem;
  border-radius: var(--radius-large);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.btn-outline-dark:hover,
.btn-outline-dark:active,
.btn-outline-dark:visited {
  color: var(--color-stardust-gold);
}

/* Primary Button (Pill Variant) */
.btn-yellow {
  background-color: var(--color-stardust-gold);
  color: white;
  font-size: 20px;
  text-decoration: none;
  padding: 13px 30px;
  font-weight: var(--font-weight-semibold);
  border-radius: var(--radius-pill-full);
  height: 55px;
  width: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-filter: var(--shadow-brand);
  filter: var(--shadow-brand);
  transition: var(--transition-standard);
  display: inline-block;
  border: none;
  cursor: pointer;
}

.btn-yellow:hover {
  color: white;
  background-color: var(--color-stardust-gold-light);
  transform: translateY(-2px);
  box-shadow: var(--shadow-gold-glow-hover);
}

.btn-yellow:active {
  transform: translateY(0);
}

.btn-yellow:focus-visible {
  outline: 3px solid var(--color-stardust-gold-light);
  outline-offset: 3px;
}

/* Secondary Button */
.btn-secondary {
  color: white;
  font-size: 20px;
  text-decoration: none;
  padding: 13px 30px;
  font-weight: var(--font-weight-semibold);
  border-radius: var(--radius-pill-full);
  height: 55px;
  width: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-filter: var(--shadow-brand);
  filter: var(--shadow-brand);
  transition: var(--transition-standard);
  display: inline-block;
  border: 2px solid var(--color-border-subtle-30);
  background-color: var(--color-charcoal-mist-50);
  cursor: pointer;
}

.btn-secondary:hover {
  background-color: var(--color-charcoal-mist-85);
  border-color: rgba(255, 255, 255, 0.6);
  transform: translateY(-2px);
  box-shadow: var(--shadow-dark);
}

.btn-secondary:focus-visible {
  outline: 3px solid var(--color-stardust-gold);
  outline-offset: 3px;
}

/* Light Button */
.btn-light {
  border-radius: var(--radius-large);
  border: none;
  color: var(--color-pure-white);
  background-color: var(--color-steel-gray);
  transition: var(--transition-standard);
}

.btn-light:hover,
.btn-light:active,
.btn-light:visited,
.btn-light:focus {
  border: none;
  color: var(--color-pure-white);
  background-color: var(--color-light-steel);
}

.btn-light.btn-donate {
  padding: 10px 20px;
  background-color: var(--color-stardust-gold);
}

.btn-light.btn-donate:hover,
.btn-light.btn-donate:active,
.btn-light.btn-donate:focus {
  background-color: var(--color-stardust-gold-light);
  color: var(--color-pure-white);
}

.btn-light.btn-donate:visited {
  background-color: var(--color-stardust-gold);
  color: var(--color-pure-white);
}

/* ============================================
   CARD COMPONENTS
   ============================================ */

.card {
  display: inline-block;
  border: 0;
  margin: var(--spacing-sm);
  padding: 0.2em;
  background-color: var(--color-charcoal-mist-70);
  border-radius: var(--radius-large);
  filter: var(--shadow-card);
  transition: var(--transition-standard);
}

.card:hover {
  transform: translateY(-4px);
  filter: var(--shadow-elevated);
  background-color: var(--color-charcoal-mist-85);
}

.hover-brightness:hover {
  filter: brightness(110%);
  transform: scale(1.02);
}

.card-group .card {
  min-width: 120px;
}

/* ============================================
   SECTION LAYOUT
   ============================================ */

section {
  width: 100%;
  margin-bottom: var(--spacing-2xl);
  padding: var(--container-padding);
  animation: fadeInUp 0.6s ease-out;
}

/* ============================================
   ANIMATIONS
   ============================================ */

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ============================================
   ACCESSIBILITY - REDUCED MOTION
   ============================================ */

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }

  html {
    scroll-behavior: auto;
  }
}
</style>
